// components/BrandLoader.js
// A lightweight CSS/vector alternative to a GIF loading animation — the real TW mark (logo-mark
// .png) breathing gently in the center of a spinning ring shaped to match the icon's own rounded
// square (not a plain circle). No file to manage/update, sharp at any size (the mark is a 1024px
// source, always downscaled here, never up), and a few hundred bytes of CSS instead of a
// multi-hundred-KB animated image.
import React from 'react';
import { COLORS } from '../theme';

// The ring is a rounded-square "donut": a conic-gradient sweep clipped down to a thin frame via
// mask-composite (an outer border-box shape XOR'd with an inner content-box shape, leaving only
// the padding-width ring visible) — border-color tricks (animating border-top/right-color) only
// look clean on a true circle; on a rounded square they produce a distorted diagonal miter at
// the corner instead of a smooth sweep.
//
// The spin itself animates the gradient's OWN angle via a registered CSS custom property
// (@property, animatable unlike a plain --var), not a `transform: rotate()` on the masked
// element. Confirmed by direct repro: rotating the masked element itself intermittently
// desyncs Chromium's mask compositing mid-animation, producing a stray unclipped streak outside
// the ring for a portion of every rotation — not a screenshot-timing fluke, a real animation
// artifact a user would actually see. Animating the gradient's angle instead leaves the mask
// static relative to its own box, sidestepping the transform+mask interaction entirely.
function BrandLoader({ title = 'Loading…', subtitle = undefined, size = 88 }) {
  const ringSize = size + 28;
  return (
    <div style={{ textAlign: 'center' }}>
      <style>{`
        @property --brandLoaderAngle {
          syntax: '<angle>';
          inherits: false;
          initial-value: 0deg;
        }
        @keyframes brandLoaderSpin { to { --brandLoaderAngle: 360deg; } }
        @keyframes brandLoaderPulse { 0%, 100% { transform: translate(-50%, -50%) scale(1); } 50% { transform: translate(-50%, -50%) scale(1.05); } }
      `}</style>
      <div style={{ position: 'relative', width: ringSize, height: ringSize, margin: '0 auto 18px auto' }}>
        <div style={{ position: 'absolute', inset: 0, borderRadius: '22%', border: '3px solid rgba(11,91,161,0.12)' }} />
        <div
          style={{
            '--brandLoaderAngle': '0deg',
            position: 'absolute', inset: 0, borderRadius: '22%',
            background: `conic-gradient(from var(--brandLoaderAngle), ${COLORS.primary} 0deg, #3FA0F0 90deg, rgba(11,91,161,0) 140deg, rgba(11,91,161,0) 360deg)`,
            padding: '3px',
            boxSizing: 'border-box',
            WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
            WebkitMaskComposite: 'xor',
            mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
            maskComposite: 'exclude',
            animation: 'brandLoaderSpin 1s linear infinite'
          }}
        />
        <img
          src="/logo-mark.png"
          alt=""
          style={{
            position: 'absolute', top: '50%', left: '50%', width: size, height: size,
            transform: 'translate(-50%, -50%)', borderRadius: '22%',
            animation: 'brandLoaderPulse 1.8s ease-in-out infinite'
          }}
        />
      </div>
      <p style={{ margin: '0 0 4px 0', fontWeight: '700', fontSize: '15px', color: COLORS.text }}>{title}</p>
      {subtitle && <p style={{ margin: 0, fontSize: '13px', color: COLORS.textFaint }}>{subtitle}</p>}
    </div>
  );
}

export default BrandLoader;
